import { buildQueryString, createNestedRepository, createRepository } from './repository';

// Import all your models
import { AutomationMessage } from '@app/model/AutomationMessage';
import { ClientExecutive } from '@app/model/ClientExecutive';
import {
  Communication,
  CommunicationChange,
  CommunicationPreview,
  CommunicationUpdate,
  RecipientSourceInfo
} from '@app/model/Communication';
import { Country } from '@app/model/Country';
import { Course } from '@app/model/Course';
import { CourseVersion } from '@app/model/CourseVersion';
import { Curriculum as Curriculum } from '@app/model/Curriculum'; // Alias to uppercase component standard
import { CustomField } from '@app/model/CustomField';
import { CustomFieldValue } from '@app/model/CustomFieldValue';
import { AUDIENCE_SCOPE, KLASS_TYPE } from '@app/model/Enums';
import { Facility, FacilityRoom } from '@app/model/Facility';
import { FlagDefinition } from '@app/model/Flag';
import { Holiday } from '@app/model/Holiday';
import {
  AuditableEntityView,
  EntityHistory,
  HistoryFeed,
  HistoryFeedParams
} from '@app/model/History';
import { Instructor } from '@app/model/Instructor';
import { AdHocKlass, Klass, OpenEnrolmentKlass, PrivateKlass } from '@app/model/Klass';
import { PipelineKlass } from '@app/model/PipelineKlass';
import { Language } from '@app/model/Language';
import { Notification } from '@app/model/Notification';
import { Parameter, ParameterChange } from '@app/model/Parameter';
import { Partner } from '@app/model/Partner';
import { PostalCodeCoverage, PostalCodeImportSummary } from '@app/model/PostalCode';
import { PrivateCourse } from '@app/model/PrivateCourse';
import { RedHatRegion } from '@app/model/RedHatRegion';
import { SavedFilter } from '@app/model/SavedFilter';
import { Skill } from '@app/model/Skill';
import { Submission, SurveyView, TranslationSummary } from '@app/model/Survey';
import { TrainingSpecialistStrategic } from '@app/model/TrainingSpecialistStrategic';
import { InstructorUtilization } from '@app/model/Utilization';
import { CLASS_EDIT_PERIOD_WEEKS, HISTORY_PAGE_SIZE, KLASS_STORE_PAGE_SIZE } from '@app/utils/config';
import { fetchData } from '@app/utils/fetchData';
import { getSundayXWeeksAgo } from '@app/utils/functions';

/**
 * Transport only: one function per endpoint, every one of them going to the network.
 *
 * Nothing here may import from '@app/store' — the stores are built on top of these
 * repositories, so a reference back the other way is a cycle, and whichever module loses the
 * race to be evaluated first hands the other undefined. Anything that should answer from the
 * cache before reaching for the network belongs in store/entityLookups.ts, which sits above
 * both and is free to use either.
 */

// --- Instantiated API Repositories ---

export const ClientExecutiveApi = createRepository<ClientExecutive>('/clientexecutive');
export const CurriculumApi = createRepository<Curriculum>('/curriculum');
export const CustomFieldApi = createRepository<CustomField>('/customfield');
export const FacilityApi = createRepository<Facility>('/facility');
export const InstructorApi = createRepository<Instructor>('/instructor');
export const LanguageApi = createRepository<Language>('/language');
export const PartnerApi = createRepository<Partner>('/partner');
export const PrivateCourseApi = createRepository<PrivateCourse>('/privatecourse');
export const RedHatRegionApi = createRepository<RedHatRegion>('/redhatregion');
export const SavedFilterApi = createRepository<SavedFilter>('/savedfilter');
export const TrainingSpecialistStrategicApi = createRepository<TrainingSpecialistStrategic>('/tss');

/**
 * Automation messages, the log of what the imports could not make sense of.
 *
 * The collection read is the whole log - see AUTOMATION_MESSAGE_STORE_PAGE_SIZE for the size the
 * store has to ask for - so anything that wants one record's worth of it goes through `getByKey`
 * rather than fetching all of it and filtering.
 */
export const AutomationMessageApi = {
  ...createRepository<AutomationMessage>('/automationmessage'),

  /**
   * Every message filed under one key, whatever entity name each was filed under and whichever
   * system reported it.
   *
   * Filtered by the service, which is what keeps this off the store: a class holds a message per
   * source at most - the table is uniquely indexed on entity, key and source together - so the
   * answer is a row or three and fits inside the endpoint's default page comfortably. No `size` is
   * sent for that reason; one would only claim a ceiling that nothing here needs.
   *
   * Not narrowed by `entity` on the wire on purpose. The entity name belongs to whichever system
   * reported the finding and has changed once already, so asking for one name is how a class's
   * messages would come back empty after the next such change; callers keep what is theirs with
   * `isKlassMessage`.
   *
   * Silent: this answers a question a page asks about a record it is already showing, and a
   * loading backdrop over a page that has finished loading reads as the page reloading itself.
   */
  getByKey: async (key: string | number): Promise<AutomationMessage[]> => {
    return fetchData(`/automationmessage${buildQueryString({ key })}`, { silent: true });
  }
};

/**
 * Countries are reference data. `/country` exposes the reads and exactly one write - the default
 * timezone, through its own endpoint - so the rest of the repository's CRUD is not merely unused
 * here, it does not exist on the service.
 *
 * Which is why create/update/remove throw rather than being left to answer 405. The store's shape
 * requires all five methods, and a caller that reaches for one has made a mistake worth naming at
 * the point it happens.
 */
export const CountryApi = {
  ...createRepository<Country>('/country'),

  create: async (): Promise<Country> => {
    throw new Error('Countries are reference data and cannot be created.');
  },

  remove: async (): Promise<void> => {
    throw new Error('Countries are reference data and cannot be deleted.');
  },

  /**
   * The one editable field, routed to the endpoint that owns it. Called by the store's `save`, so
   * `useCountryStore.save(country, false)` writes the timezone and nothing else - which is the
   * whole of what a country update means.
   */
  update: async (id: string | number, data: Partial<Country>): Promise<Country> => {
    return CountryApi.setTimezone(id, data.timezone ?? '');
  },

  /**
   * Sets the country's default timezone: the zone a date-only value arriving for a class in this
   * country is read in.
   *
   * Admin only, and the zone has to be one of the country's own `timezones` - a zone that parses
   * perfectly but belongs elsewhere is refused, because accepting it would silently put every class
   * in that country hours out. Returns the country as it now stands, so no follow-up read is needed.
   */
  setTimezone: async (id: string | number, timezone: string): Promise<Country> => {
    return fetchData(`/country/${id}/timezone`, { method: 'PUT', data: { timezone } });
  }
};

export const CourseApi = {
  ...createRepository<Course>('/course'),

  getBySku: async (sku: string): Promise<Course> => {
    return fetchData(`/course/sku/${sku}`, { method: 'GET' });
  },

  getCertifications: async (): Promise<Course[]> => {
    return fetchData('/course/certification', { method: 'GET' });
  }
};

export const FlagDefinitionApi = {
  // Get all the standard CRUD methods
  ...createRepository<FlagDefinition>('/flagdefinition'),
  
  preview: async (expression: string, targetEntity: string): Promise<any[]> => {
    return fetchData('/flagdefinition/preview', {
      method: 'POST', 
      data: { expression, targetEntity },
      silent: true
    });
  },

  getDictionary: async (): Promise<Record<string, string[]>> => {
    return fetchData('/flagdefinition/dictionary', { method: 'GET', silent: true });
  },
  
  validateSyntax: async (expression: string): Promise<void> => {
    return fetchData('/flagdefinition/validate', { method: 'POST', data: { expression }, silent: true });
  }
};

/**
 * `?region=all` - every holiday the service holds, whichever subdivision observes it.
 *
 * Without a `region` parameter the holiday endpoints answer with the nationwide days only,
 * which is what they answered before regional holidays existed and what every view but the
 * administration screen wants: opting in multiplies the collection several-fold with days that
 * are not observed where most readers are. Spain alone goes from about a dozen days a year to
 * near fifty.
 *
 * Whatever is passed to a list has to be passed to the matching count as well - see
 * `createRepository`'s `count`.
 */
export const ALL_REGIONS = 'all';

export const HolidayApi = {
  // Standard CRUD methods. `getAll` and `count` take `{ region }` like any other parameter;
  // POST and PUT answer 409 when the country, date and region together already exist.
  ...createRepository<Holiday>('/holiday'),

  /**
   * One country's holidays for one year, which is how the Calendar loads the days it draws.
   *
   * `region` is omitted there, and deliberately: the overlay shows the days a country as a
   * whole observes. Pass `ALL_REGIONS` only somewhere that means to show every subdivision's.
   */
  getByCountryAndYear: async (countryId: string, year: number, region?: string): Promise<any> => {
    return fetchData(`/holiday/${countryId}/${year}${buildQueryString({ region })}`, { method: 'GET' });
  }
};

/** Query parameters GET /notifications accepts. All optional. */
export interface NotificationSearchParams {
  /** 0 based. */
  page?: number;
  /** Clamped by the service's NOTIFICATION_MAX_PAGE_SIZE, which defaults to 200. */
  size?: number;
  /** Only the ones the caller has not read. */
  unread?: boolean;
  /** Only notifications newer than this id — how a reconnecting client catches up. */
  sinceId?: number | null;
}

/**
 * Body of POST /notifications.
 *
 * Carries no type: an announcement is always the service's ANNOUNCEMENT constant, and a client able
 * to name its own type could forge a system failure. Severity is fixed at INFO for the same reason.
 */
export interface AnnouncementPayload {
  scope: AUDIENCE_SCOPE;
  /**
   * A username for USER, a role for ROLE. Must be absent for ALL — the service rejects the wrong
   * pairing rather than ignoring the spare value, because "everybody, in particular Ricardo" is a
   * contradiction and guessing which half to keep would deliver to the wrong audience.
   *
   * For ROLE it must also be one of the addressable roles, which is what `audiences()` returns.
   */
  audience?: string | null;
  title: string;
  /**
   * Required, and not optional as it first appears: the service rejects a blank message with a 400,
   * on the grounds that a notification with only a title says what happened but not what to do
   * about it. Typed non-optional so that is a compile error here rather than a bodyless 400 there.
   */
  message: string;
  /**
   * When it stops being shown, as an ISO-8601 instant. Worth setting on anything time-bound: it is
   * what retires "maintenance tonight" tomorrow instead of leaving it in every inbox for the whole
   * retention window. Must be in the future — a past one is a 400.
   */
  expiresAt?: string | null;
}

/**
 * The caller's own notification inbox.
 *
 * Not a createRepository, and the shape of it is a deliberate part of the service's contract rather
 * than an omission. There is no PUT and no DELETE: a notification row is shared by everybody it is
 * addressed to, so `DELETE /notifications/{id}` — the mapping anybody would reach for first — would
 * destroy other people's mail. Hiding one for yourself is `POST /{id}/dismiss`, which writes a
 * receipt against your username and touches nobody else's inbox; removing one for everyone is
 * `POST /{id}/retract` and is limited to the announce roles.
 *
 * Every read here is `silent`: the bell loads itself in the background on every sign-in and on every
 * socket reconnect, and none of that is worth a loading backdrop over the page the user is on.
 * The writes are silent for the same reason — the panel updates optimistically, so a spinner would
 * only ever cover an interaction that has already visibly happened.
 */
export const NotificationApi = {
  /** Newest first. Never includes what the caller has dismissed, nor anything past its expiry. */
  list: async (params?: NotificationSearchParams): Promise<Notification[]> => {
    return fetchData(`/notifications${buildQueryString({ ...params })}`, { silent: true, noCache: true });
  },

  /**
   * How many the caller can see, or how many of those are unread.
   *
   * The authority for the badge count. It counts the whole inbox rather than the page the panel
   * happens to have loaded, which is the one thing a client-side tally of `items` cannot do.
   */
  count: async (unread = false): Promise<number> => {
    return fetchData(`/notifications/count${buildQueryString({ unread })}`, { silent: true, noCache: true });
  },

  markRead: async (id: number): Promise<Notification> => {
    return fetchData(`/notifications/${id}/read`, { method: 'POST', silent: true });
  },

  markUnread: async (id: number): Promise<Notification> => {
    return fetchData(`/notifications/${id}/unread`, { method: 'POST', silent: true });
  },

  /** @returns how many receipts were written */
  markAllRead: async (): Promise<number> => {
    return fetchData('/notifications/read-all', { method: 'POST', silent: true });
  },

  /** Hides it from this caller and nobody else. Idempotent, and there is no endpoint that undoes it. */
  dismiss: async (id: number): Promise<void> => {
    return fetchData(`/notifications/${id}/dismiss`, { method: 'POST', silent: true });
  },

  /**
   * Publishes an announcement.
   *
   * Not silent, unlike everything above: this is a write somebody asked for and is waiting on, and
   * the one call here where the backdrop is the right feedback.
   *
   * Who may call it is decided by the service against its NOTIFICATION_ANNOUNCE_ROLES parameter
   * rather than by a role annotation, so a 403 here is a legitimate answer for an authenticated
   * user and the caller has to be ready for it.
   */
  announce: async (payload: AnnouncementPayload): Promise<Notification> => {
    return fetchData('/notifications', { method: 'POST', data: payload });
  },

  /**
   * The roles an announcement may be addressed to, which is what the recipient picker is built
   * from. Read from the service rather than assembled here on purpose: it returns the same
   * NOTIFICATION_AUDIENCE_ROLES parameter the announcement is validated against, so the picker
   * cannot offer a role the write would then reject.
   */
  audiences: async (): Promise<string[]> => {
    return fetchData('/notifications/audiences', { silent: true });
  }
};

/**
 * Runtime parameters. Not a createRepository: the endpoint is addressed by the catalog key
 * rather than a row id, has no POST — the catalog is closed, so a parameter cannot be created —
 * and DELETE means "drop the override" rather than "delete the setting".
 *
 * `key` accepts either spelling, the catalog constant (`SEARCH_MAX_PAGE_SIZE`) or the property
 * name (`cas.search.max-page-size`). Encoded on the way out because the property spelling
 * carries dots and could one day carry worse.
 *
 * Reads are uncached: this page exists to show what the service is using right now, and a
 * browser answering a repeat GET from its cache would show the state before somebody else's
 * change. Writes return the resulting view, so nothing here needs a follow-up GET.
 */
export const ParameterApi = {
  getAll: async (): Promise<Parameter[]> => {
    return fetchData('/parameters', { noCache: true });
  },

  getByKey: async (key: string): Promise<Parameter> => {
    return fetchData(`/parameters/${encodeURIComponent(key)}`, { noCache: true });
  },

  /** Sets an override. The body is always a JSON string, whatever type the key declares. */
  set: async (key: string, value: string): Promise<Parameter> => {
    return fetchData(`/parameters/${encodeURIComponent(key)}`, { method: 'PUT', data: { value } });
  },

  /** Drops the override, so the setting falls back to the configured value. Idempotent. */
  reset: async (key: string): Promise<Parameter> => {
    return fetchData(`/parameters/${encodeURIComponent(key)}`, { method: 'DELETE' });
  },

  history: async (key: string): Promise<ParameterChange[]> => {
    return fetchData(`/parameters/${encodeURIComponent(key)}/history`, { noCache: true });
  },

  /** Rows left behind by a parameter that was renamed or removed. Nothing reads them. */
  orphans: async (): Promise<string[]> => {
    return fetchData('/parameters/orphans', { noCache: true });
  },

  removeOrphan: async (key: string): Promise<boolean> => {
    return fetchData(`/parameters/orphans/${encodeURIComponent(key)}`, { method: 'DELETE' });
  }
};

/**
 * The postal code reference data the address parse reads: `/postal-code`.
 *
 * Two calls and no more, because the service exposes two. Not a createRepository for the reason
 * model/PostalCode gives: the table is published reference data with no row read, no search and no
 * CRUD, so there is nothing for the other three methods to do and offering them would only invite
 * a caller to try.
 *
 * **`runImport` takes no argument, and must not grow one.** The file it loads is named by the
 * server-side `cas.postal-code.source-file` setting, deliberately, so that what a pod reads off its
 * own disk is part of its deployment. An endpoint taking a path would let any admin make the service
 * read any file it can see, which is why there is no path parameter to pass one to.
 *
 * Reads are uncached, like the parameters above: the page exists to show what is loaded right now,
 * and a browser answering a repeat GET from its cache would show the state before somebody else's
 * import.
 */
export const PostalCodeApi = {
  getCoverage: async (): Promise<PostalCodeCoverage[]> => {
    return fetchData('/postal-code/coverage', { noCache: true });
  },

  /**
   * Loads the configured export, replacing the codes of every country it carries.
   *
   * **Synchronous, and it takes a minute or two** on the combined file - there is no job to poll.
   * `silent` for that reason: the global loading backdrop would sit over the page for two minutes
   * saying nothing, so the caller shows a blocking progress state of its own that names what is
   * happening. Do not retry a failure automatically; a retry mid-import queues a second full load.
   */
  runImport: async (): Promise<PostalCodeImportSummary> => {
    return fetchData('/postal-code/import', { method: 'POST', silent: true });
  }
};

/**
 * The communications the service can send: `/communications`.
 *
 * The same shape as ParameterApi, and for the same reasons — the catalog is the service's, so
 * there is no POST that takes a name, nothing is addressed by row id, and DELETE means "drop the
 * override" rather than "delete the communication".
 *
 * The one difference worth knowing at the call site: **`set` is a full replacement**. A null field
 * in the body means "no override", so a caller has to send the whole communication back. Build the
 * body with `updateFrom` in model/Communication rather than by hand.
 */
export const CommunicationApi = {
  getAll: async (): Promise<Communication[]> => {
    return fetchData('/communications', { noCache: true });
  },

  getByKey: async (key: string): Promise<Communication> => {
    return fetchData(`/communications/${encodeURIComponent(key)}`, { noCache: true });
  },

  /**
   * The sources a recipient list may be addressed with, read from the enum rather than assembled
   * here so the editor's picker cannot drift from what a PUT will accept — the same argument
   * NotificationApi.audiences makes.
   */
  recipientSources: async (): Promise<RecipientSourceInfo[]> => {
    return fetchData('/communications/recipient-sources', { silent: true });
  },

  /** Sets the override, whole. Returns the resulting communication, not 204: the caller's next
   * question is always "so what does it say now, and what is still tracking the code". */
  set: async (key: string, update: CommunicationUpdate): Promise<Communication> => {
    return fetchData(`/communications/${encodeURIComponent(key)}`, { method: 'PUT', data: update });
  },

  /** Drops the override, so the communication goes back to what the catalog declares. Idempotent. */
  reset: async (key: string): Promise<Communication> => {
    return fetchData(`/communications/${encodeURIComponent(key)}`, { method: 'DELETE' });
  },

  /**
   * Renders a communication without sending it, against sample values. Placeholders left out are
   * rendered as their own name in braces, so the output shows where a value would go.
   *
   * Renders what is *stored*, not what is on screen, so it answers "what does this communication
   * say now" rather than "what would my edit produce".
   */
  preview: async (key: string, values: Record<string, string>): Promise<CommunicationPreview> => {
    return fetchData(`/communications/${encodeURIComponent(key)}/preview`, { method: 'POST', data: values });
  },

  history: async (key: string): Promise<CommunicationChange[]> => {
    return fetchData(`/communications/${encodeURIComponent(key)}/history`, { noCache: true });
  },

  /** Rows left behind by a communication that was renamed or removed. Nothing reads them. */
  orphans: async (): Promise<string[]> => {
    return fetchData('/communications/orphans', { noCache: true });
  },

  /** @returns the orphans that are left */
  removeOrphan: async (key: string): Promise<string[]> => {
    return fetchData(`/communications/orphans/${encodeURIComponent(key)}`, { method: 'DELETE' });
  }
};

/**
 * The audit trail: `/history`.
 *
 * Not a repository, because it is not CRUD - there is nothing to create and nothing addressed by
 * a row id of its own. One resource serves every entity, and the response shape does not vary
 * with the entity, which is why there is one function here and not one per page.
 *
 * Reads are uncached, like the parameter and communication histories: the question is what the
 * service has recorded right now, and a browser answering a repeat GET from its cache would hide
 * a revision somebody else has just caused.
 *
 * `entity` is a slug the service matches against its own registry - `class`, `instructor` - and it
 * never loads a class name a caller sent, so an unregistered slug is a 404 rather than anything
 * worse. A registered entity this caller's roles do not cover is a 403, deliberately distinct from
 * the 404: telling somebody "no history exists" when the truth is "not for you" sends them looking
 * for the wrong bug. Both reach the page as an ApiError - use isNotFound / isForbidden.
 */
export const HistoryApi = {
  /**
   * The entities this caller may read the history of, already filtered by their roles.
   *
   * Drive the presence of a history view off this rather than off a list here: the registry is the
   * service's, so an entity added there appears without a change on this side, and one the user
   * may not read never offers a button that would answer 403.
   */
  entities: async (): Promise<AuditableEntityView[]> => {
    return fetchData('/history', { noCache: true, silent: true });
  },

  /**
   * One row's history, newest revision first.
   *
   * `size` is what we ask for, not what we are held to: the service clamps anything above
   * HISTORY_MAX_PAGE_SIZE instead of refusing it, and `hasMore` on the answer is the only
   * reliable way to know whether to offer another page - a short page does not mean the last one.
   */
  forEntity: async (
    entity: string,
    id: string | number,
    page = 0,
    size = HISTORY_PAGE_SIZE
  ): Promise<EntityHistory> => {
    return fetchData(
      `/history/${encodeURIComponent(entity)}/${id}${buildQueryString({ page, size })}`,
      { noCache: true }
    );
  },

  /**
   * What changed recently across everything, newest revision first.
   *
   * Reports revisions to types the caller may not read as well, by type name alone with no ids and
   * no slug to follow: omitting them would present an incomplete timeline as a complete one.
   *
   * `from` and `to` are ISO 8601 instants and malformed ones are a 400, never silently dropped -
   * so a date picker must send an instant or send nothing.
   */
  feed: async (params: HistoryFeedParams = {}): Promise<HistoryFeed> => {
    const { username, entity, from, to, page = 0, size = HISTORY_PAGE_SIZE } = params;
    return fetchData(
      `/history/feed${buildQueryString({ username, entity, from, to, page, size })}`,
      { noCache: true }
    );
  }
};

/**
 * Class surveys.
 *
 * **Read only, and permanently so.** The service has no write endpoint and is not going to grow
 * one: surveys arrive on a topic, upstream owns them, and there is no createRepository here for
 * that reason - it would come with a create, an update and a remove that all answer 405.
 *
 * The two halves are not equally readable and the split is by endpoint, not by field:
 * {@link getByKlass} and {@link getByOffering} answer the scores and the anonymised comments to any
 * signed-in user, while {@link getSubmissions} and {@link getSubmission} answer the identified
 * respondent data and need `admin`. A page must ask for the second separately and be able to render
 * without it - see ClassSurvey.tsx, and isForbidden in utils/ApiError for recognising the refusal.
 *
 * A class with no survey is a 404, and so is a class that does not exist: the service does not
 * distinguish them, because either way there is no survey to answer with. Handle it as the answer
 * it is rather than as a failure - most classes have no survey until well after they are delivered.
 */
export const SurveyApi = {
  /** Every survey. Not used by the class page; here because the endpoint exists. */
  getAll: async (): Promise<SurveyView[]> => fetchData('/survey'),

  count: async (): Promise<number> => fetchData('/survey/count', { silent: true }),

  /** One survey by its own id. */
  getById: async (id: number | string): Promise<SurveyView> => fetchData(`/survey/${id}`),

  /**
   * The survey for a class, by the class's **own id** - `klass.id`, not the offering id the class
   * page is addressed by. This is the call the class page makes.
   */
  getByKlass: async (klassId: number | string): Promise<SurveyView> =>
    fetchData(`/survey/class/${klassId}`),

  /** The same, for a caller that only holds the offering id the LMS and the survey platform use. */
  getByOffering: async (offeringId: number | string): Promise<SurveyView> =>
    fetchData(`/survey/offering/${offeringId}`),

  /**
   * Every submission on a survey, in full - answers, ratings, comments and the respondent.
   *
   * **`admin` only**, and a 403 for everybody else. Takes the *survey's* id, which is what
   * {@link getByKlass} answered with, so a caller reaches it in two requests rather than one.
   */
  getSubmissions: async (surveyId: number | string): Promise<Submission[]> =>
    fetchData(`/survey/${surveyId}/submission`),

  /** One submission by the uuid the survey platform gave it. `admin` only, as above. */
  getSubmission: async (uuid: string): Promise<Submission> =>
    fetchData(`/survey/submission/${encodeURIComponent(uuid)}`),

  /**
   * Translates every comment on a class's survey into English and stores the result beside each
   * original - see `TranslatedComment` in model/Survey.ts. The one write in this file's survey
   * section, and the only thing anywhere that causes a translation to happen.
   *
   * Takes the class's **own id**, like every other `/class/{id}` path here and unlike the address
   * the survey page is reached by: the offering id answers 404, because offering lookups go
   * through `/class/offeringId/{oid}` instead. The page holds both, having loaded the class first.
   *
   * `force` redoes comments that already carry a translation. Without it the service leaves those
   * alone - they come back counted as `skipped` - which is what makes the option safe to press
   * twice by accident and why the page asks before sending it.
   */
  translateComments: async (klassId: number | string, force = false): Promise<TranslationSummary> =>
    fetchData(`/class/${klassId}/survey/translate${force ? '?force=true' : ''}`, { method: 'POST' })
};

// ---------------------------------------------------------
// 2. NESTED REPOSITORIES (Parent-Child)
// ---------------------------------------------------------
export const FacilityRoomApi = createNestedRepository<FacilityRoom>('facility', 'room');
export const SkillApi = createNestedRepository<Skill>('instructor', 'skill');
export const CustomFieldValueApi = createNestedRepository<CustomFieldValue>('instructor', 'customfield');
export const CourseVersionApi = createNestedRepository<CourseVersion>('course', 'version');

/**
 * Asking for more rows than GET /class/search allows is not an error: the endpoint silently
 * clamps it. So a caller that pages must not read a page shorter than the one it asked for as
 * "the last page" - it may simply have asked for more than the server allows. Stop on an empty
 * page instead.
 *
 * The sizes themselves - KLASS_STORE_PAGE_SIZE, KLASS_SEARCH_MAX_PAGE_SIZE and the rest - live in
 * utils/config.ts, together and with the headroom each one has.
 */

/**
 * Every parameter GET /class/search accepts. All optional and combined with `and`; the
 * array ones match any of their values.
 *
 * Notes that are not obvious from the names:
 * - `startDate` keeps classes starting on that day or later, `endDate` classes ending on
 *   that day or earlier, so a pair of dates means "runs entirely within the range".
 * - `week` is an ISO week (1-53) and matches every class *running* during it, including
 *   one that started the week before. `year` only qualifies `week`, and defaults to the
 *   current year.
 * - `instructorId` matches the instructor of record only, not the allocated or the final one.
 * - `requestId` exists on private and ad hoc classes - `REQnnnn` and `ADRnnnn` respectively - and
 *   both are matched, so filtering by it excludes open enrolment classes and nothing else. It is
 *   also how the class a pipeline request produced is found, since the two share the id.
 * - `page` is 0 based. `sort` takes entity field names, `-` prefixed for descending, and
 *   rejects anything outside the endpoint's sortable set with a 400.
 */
export interface KlassSearchParams {
  type?: string[];
  status?: string[];
  courseId?: number[];
  instructorId?: number[];
  languageId?: number[];
  clientExecutiveId?: number[];
  offeringId?: number[];
  requestId?: string[];
  startDate?: string | null;
  endDate?: string | null;
  week?: number | null;
  year?: number | null;
  sort?: string[];
  page?: number;
  size?: number;
}

// --- Special Cases for Polymorphic/Inherited Entities ---
// You likely use the base '/class' endpoint for all class types,
// but you can type them specifically if needed.
/**
 * The first day the class store keeps. Read by getAll and by the completeness probe below, so
 * the two cannot end up measuring different windows.
 */
const classWindowStart = () => getSundayXWeeksAgo(CLASS_EDIT_PERIOD_WEEKS);

export const KlassApi = {
  ...createRepository<Klass>('/class'),

  // this method will be overriden to set a limit of 5000 and define a search by date by default, limiting
  // its scope, unless explicity called. The window is CLASS_EDIT_PERIOD_WEEKS: the store holds exactly
  // the classes that may still be edited, and anything older belongs to Classes History.
  getAll: async (params?: Record<string, string | number | boolean | null | undefined>): Promise<Klass[]> => {
    // 2. Manually merge your strict defaults with whatever the store/component passes.
    // By putting ...params last, the component can still override size or startDate if it explicitly wants to!
    const mergedParams = {
      size: KLASS_STORE_PAGE_SIZE,
      startDate: classWindowStart(),
      ...params
    };

    return fetchData(`/class/search${buildQueryString(mergedParams)}`);
  },

  /**
   * Whether the window getAll fetches holds more than `limit` classes — that is, whether the
   * store is now dropping classes it was asked to hold.
   *
   * `count()` cannot answer this. GET /class/count takes no parameters, so it counts every class
   * ever scheduled, while the store only asks for the CLASS_EDIT_PERIOD_WEEKS window; comparing
   * the two would report a problem permanently. So this asks the search endpoint for one row at
   * offset `limit` instead: 0 based paging means page=limit with size=1 is row limit+1, and
   * getting it back proves there is at least one class the window cannot hold. One row on the
   * wire, exact answer, and it honours the same filter getAll uses.
   *
   * If the service ever grows a GET /class/search/count, prefer it — it would give the total
   * rather than just "more than this", which is the one thing this cannot report.
   */
  exceedsWindow: async (limit: number): Promise<boolean> => {
    const beyond = await fetchData(
      `/class/search${buildQueryString({ startDate: classWindowStart(), page: limit, size: 1 })}`,
      { silent: true, noCache: true }
    );
    return Array.isArray(beyond) && beyond.length > 0;
  },

  /**
   * The search endpoint as it is, with none of getAll's defaults: no size, no four week
   * floor, whatever the caller asks for. Answers from the network every time - neither the
   * entity store nor the browser cache is consulted or written - so the caller owns the
   * result and is responsible for paging it. Used by the Classes History page, which must
   * reach classes older than the store's window and must never show a stale row.
   */
  search: async (params: KlassSearchParams): Promise<Klass[]> => {
    return fetchData(`/class/search${buildQueryString({ ...params })}`, { noCache: true });
  },

  getByOfferingId: async (oid: string | number): Promise<Klass> => {
    return fetchData(`/class/offeringId/${oid}`);
  },

  /**
   * By primary key, which is what everything *except* the pages addresses a class by: the audit
   * trail and the entity update socket both report the id, while the detail page is routed on the
   * offering id. So this is how an id from one of those is turned into somewhere to navigate.
   *
   * Not silent, like the repository's own getById that CourseApi answers the same question with:
   * the click costs a request before anything moves, and the loading indicator is what says so.
   */
  getById: async (id: string | number): Promise<Klass> => {
    return fetchData(`/class/${id}`);
  },

  update: async (id: number | string, klass: Partial<Klass>): Promise<Klass> => {
    // Safety check: We can't route the request if the payload doesn't include the class type
    if (!klass.type) {
      throw new Error("Class 'type' is required in the payload to determine the correct update endpoint.");
    }

    switch (klass.type) {
      case KLASS_TYPE.OPEN_ENROLMENT:
        return await OpenEnrolmentKlassApi.update(id, klass as OpenEnrolmentKlass);
      case KLASS_TYPE.PRIVATE:
        return await PrivateKlassApi.update(id, klass as PrivateKlass);
      case KLASS_TYPE.AD_HOC:
        return await AdHocKlassApi.update(id, klass as AdHocKlass);
      default:
        throw new Error(`Unsupported class type: ${klass.type}`);
    }
  },

  release: async (id: number): Promise<void> => {
    return fetchData(`/class/${id}/release`, { method: 'POST' });
  }
};

// Private Class API + multiple custom lookups
export const PrivateKlassApi = {
  ...createRepository<PrivateKlass>('/class/private'),

  getByOfferingId: async (oid: string | number): Promise<PrivateKlass> => {
    return fetchData(`/class/private/offeringId/${oid}`);
  },

  getByRequestId: async (reqId: string | number): Promise<PrivateKlass> => {
    return fetchData(`/class/private/requestid/${reqId}`);
  }
};

export const OpenEnrolmentKlassApi = createRepository<OpenEnrolmentKlass>('/class/oe');

/**
 * An ad hoc class is an open enrolment class, so it is already reachable through `/class` and
 * `/class/search` like any other. This endpoint is what makes the request half of it - the ticket,
 * the justification, who asked and who approved - writable: those two are typed on Klass.
 */
export const AdHocKlassApi = {
  ...createRepository<AdHocKlass>('/class/adhoc'),

  /** `ADRnnnn`. Uniquely indexed, so this answers one class or a 404. */
  getByRequestId: async (reqId: string): Promise<AdHocKlass> => {
    return fetchData(`/class/adhoc/requestId/${reqId}`);
  }
};

/**
 * Pipeline requests: what is asked for before a class exists.
 *
 * NOT under `/class`, and that is the service's contract rather than an oversight. The class
 * subtypes sit at `/class/oe`, `/class/private` and `/class/adhoc` because each of them really is a
 * class; a pipeline request precedes one, has no offering, and is absent from `/class/search`.
 */
export const PipelineKlassApi = {
  ...createRepository<PipelineKlass>('/pipelineclass'),

  /**
   * A request by the id the outside world holds rather than its row id - `REQnnnn` for one that
   * becomes a private class, `ADRnnnn` for an ad hoc one. Uniquely indexed, so this answers one
   * request or a 404.
   */
  getByRequestId: async (reqId: string): Promise<PipelineKlass> => {
    return fetchData(`/pipelineclass/requestId/${reqId}`);
  }
};


/**
 * Liveness check used by the connectivity banner to find out when the service is
 * back. Any cheap, always-present endpoint will do — swap this for a dedicated
 * health route if the service ever exposes one. Resolving or rejecting is enough;
 * fetchData reports the outcome to the health store either way.
 */
export const pingApi = async (): Promise<void> => {
  await fetchData('/country?size=1', { method: 'GET', silent: true });
};

/** What the service reports about itself: its application name and the version deployed. */
export interface ApiVersionInfo {
  name: string;
  version: string;
}

/**
 * The version of the backend this session is talking to, for the About dialog and the
 * sidebar stamp. Silent: nothing here is worth a loading backdrop, and a service too old
 * to have the endpoint answers 404 — a 'client' error, so it stays out of the health banner.
 */
export const getApiVersion = async (): Promise<ApiVersionInfo> => {
  return fetchData('/version', { method: 'GET', silent: true });
};

/** What {@link InstructorUtilizationApi.search} asks for. */
export interface InstructorUtilizationParams {
  /**
   * Whose utilization to report. Omitted or empty means every instructor, which is the whole
   * aggregation and still answers in milliseconds - there is no reason to avoid it.
   */
  instructorId?: number[];
  /** First day to count, inclusive, as `YYYY-MM-DD`. */
  from: string;
  /** Last day to count, inclusive. */
  to: string;
}

/**
 * How many days each instructor spent delivering, per month.
 *
 * **Months with no delivery are absent, not zero**, and `deliveredDays` is a decimal - half days
 * are real. model/Utilization.ts holds both of those decisions; see docs/README-utilization.md in
 * class-allocation-service for how a day is counted.
 *
 * `/instructor/utilization` is a literal segment beside the templated `/instructor/{id}`, which
 * JAX-RS resolves in favour of the literal - the same arrangement `/instructor/search` relies on.
 * It moved here from `/dashboard/instructor-utilization`, which no longer answers.
 */
export const InstructorUtilizationApi = {
  /**
   * The report: every instructor asked for, in one request.
   *
   * `noCache`, like the class history search: the Classes Outlook import restates and backdates
   * classes continuously, so the same URL asked twice is a different answer and a browser cache
   * hit would report a corrected class as it was.
   */
  search: async (params: InstructorUtilizationParams): Promise<InstructorUtilization[]> => {
    const { instructorId, from, to } = params;
    return fetchData(
      `/instructor/utilization${buildQueryString({ instructorId, from, to })}`,
      { noCache: true }
    );
  },

  /** One instructor's own, beside their skills and custom fields. Here because the endpoint exists. */
  forInstructor: async (id: number, from: string, to: string): Promise<InstructorUtilization[]> => {
    return fetchData(`/instructor/${id}/utilization${buildQueryString({ from, to })}`, { noCache: true });
  }
};

// Automation API
export const AutomationApi = {
  triggerProcess: async (process: string, body: string | undefined = undefined, contentType : string = 'text/plain') => {
      return fetchData(`/automation/${process}`, {method: 'POST', data: body, contentType: contentType});
  }
};